import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusIcon, PrinterIcon, TrashIcon, PencilSimpleIcon, ReceiptIcon, XIcon, DownloadSimpleIcon, UserPlusIcon, FilePdfIcon } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const INDIAN_OIL_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/8hjjzz28_IndianOilLogo800x600.webp";
const INDANE_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/2fd21ssu_b2f93328dedc3d7c86967e492aa03b39-removebg-preview.png";

const iso = (d) => d.toISOString().slice(0, 10);
const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dash = (v) => (v ? String(v) : "—");

const EMPTY_LINE = { description: "", hsn_sac: "", date: "", qty: 0, unit: "Nos", rate: 0, amount: 0 };

const TYPE_LABEL = {
  gstin:   "GST-Tax Invoice",
  no_tax:  "Invoice",
  challan: "Delivery Challan",
  custom:  "Custom Invoice",
};

const THEMES = {
  1: { name: "Classic",  border: "#000000", accent: "#000000", head: "#FFFFFF", ink: "#000000" },
  2: { name: "Indane",   border: "#003C71", accent: "#FF7900", head: "#FFF4EA", ink: "#0F172A" },
  3: { name: "Sepia",    border: "#5B3A1E", accent: "#8B4513", head: "#FAF3E7", ink: "#3A2410" },
  4: { name: "Slate",    border: "#1E293B", accent: "#0EA5E9", head: "#EFF6FF", ink: "#0F172A" },
};

/* ═══════════════════════════ PAGE ═══════════════════════════ */
export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [cylTypes, setCylTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [editing, setEditing] = useState(null);
  const [kindFilter, setKindFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [inv, prof, cust, ctypes] = await Promise.all([
        api.get("/invoices"),
        api.get("/business-profile"),
        api.get("/customers"),
        api.get("/cylinder-types"),
      ]);
      setInvoices(inv.data || []);
      setProfile(prof.data);
      setCustomers(cust.data || []);
      setCylTypes(ctypes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load invoices");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => invoices.filter((i) => kindFilter === "all" || i.type === kindFilter),
    [invoices, kindFilter]
  );

  const del = async (id) => {
    if (!confirm("Delete this invoice?")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="border-b border-slate-200 pb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline text-brand-orange-ink">Billing · Owner only</div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-brand-blue">Invoices</h1>
          <p className="text-slate-500 mt-2 max-w-2xl text-sm">
            Generate GST-Tax Invoice, Invoice, Delivery Challan or a fully-editable custom invoice.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="rounded-none w-44" data-testid="invoice-kind-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All invoices</SelectItem>
              <SelectItem value="gstin">GST-Tax Invoice</SelectItem>
              <SelectItem value="no_tax">Invoice</SelectItem>
              <SelectItem value="challan">Delivery Challan</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setEditing({})} className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="new-invoice-button">
            <PlusIcon size={14} className="mr-2" /> New invoice
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-blue-tint">
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Invoice No.</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Type</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Date</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Bill To</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Total</TableHead>
              <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">Loading…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No invoices yet.</TableCell></TableRow>}
            {filtered.map((inv) => (
              <TableRow key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-mono-num font-semibold text-brand-blue">{inv.invoice_no}</TableCell>
                <TableCell><span className="px-2 py-0.5 text-xs uppercase tracking-wider font-semibold bg-slate-100 text-slate-700">{TYPE_LABEL[inv.type] || inv.type}</span></TableCell>
                <TableCell className="font-mono-num text-xs text-slate-600">{inv.invoice_date}</TableCell>
                <TableCell className="font-semibold">{inv.bill_to_name}</TableCell>
                <TableCell className="text-right font-mono-num font-bold text-brand-blue">{money(inv.total_amount)}</TableCell>
                <TableCell className="text-right space-x-1">
                  <button onClick={() => setPreviewInvoice(inv)} className="text-brand-blue hover:text-brand-orange-ink p-1" title="Print / preview" data-testid={`invoice-print-${inv.id}`}><PrinterIcon size={16} /></button>
                  <button onClick={() => setEditing(inv)} className="text-slate-500 hover:text-brand-blue p-1" title="Edit" data-testid={`invoice-edit-${inv.id}`}><PencilSimpleIcon size={16} /></button>
                  <button onClick={() => del(inv.id)} className="text-slate-400 hover:text-[#FF2400] p-1" title="Delete" data-testid={`invoice-delete-${inv.id}`}><TrashIcon size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing !== null && (
        <InvoiceEditor
          initial={editing}
          customers={customers}
          cylTypes={cylTypes}
          profile={profile}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onCustomerAdded={load}
        />
      )}
      {previewInvoice && (
        <InvoicePrintModal invoice={previewInvoice} profile={profile} onClose={() => setPreviewInvoice(null)} />
      )}
    </div>
  );
}

/* ═══════════════════════════ EDITOR ═══════════════════════════ */
function InvoiceEditor({ initial, customers, cylTypes, profile, onClose, onSaved, onCustomerAdded }) {
  const isEdit = !!initial?.id;
  const [type, setType] = useState(initial.type || "gstin");
  const [theme, setTheme] = useState(initial.theme || 1);
  const [invoiceNo, setInvoiceNo] = useState(initial.invoice_no || "");
  const [invoiceDate, setInvoiceDate] = useState(initial.invoice_date || iso(new Date()));
  const [customerId, setCustomerId] = useState(initial.customer_id || "");
  const [billName, setBillName] = useState(initial.bill_to_name || "");
  const [billAddress, setBillAddress] = useState(initial.bill_to_address || "");
  const [billGstin, setBillGstin] = useState(initial.bill_to_gstin || "");
  const [contactName, setContactName] = useState(initial.contact_name || "");
  const [nameOfWork, setNameOfWork] = useState(initial.name_of_work || "");
  const [customTitle, setCustomTitle] = useState(initial.custom_title || "");
  const [sgstRate, setSgstRate] = useState(String(initial.sgst_rate ?? 9));
  const [cgstRate, setCgstRate] = useState(String(initial.cgst_rate ?? 9));
  const [note, setNote] = useState(initial.note || "");
  const [lines, setLines] = useState(initial.lines?.length ? initial.lines : [{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showFetch, setShowFetch] = useState(false);
  // Auto-fetch inline range (used when customer + both dates are set)
  const [autoFrom, setAutoFrom] = useState(iso(new Date(Date.now() - 30 * 86400000)));
  const [autoTo, setAutoTo] = useState(iso(new Date()));
  const [autoFetching, setAutoFetching] = useState(false);
  const [lastAutoKey, setLastAutoKey] = useState("");

  const setLine = (idx, patch) =>
    setLines((rows) => rows.map((r, i) => {
      if (i !== idx) return r;
      const nxt = { ...r, ...patch };
      const qty = parseFloat(nxt.qty) || 0;
      const rate = parseFloat(nxt.rate) || 0;
      if (!("amount" in patch)) nxt.amount = +(qty * rate).toFixed(2);
      return nxt;
    }));
  const addLine = () => setLines((rs) => [...rs, { ...EMPTY_LINE }]);
  const removeLine = (i) => setLines((rs) => rs.filter((_, idx) => idx !== i));

  // Sort by date ascending for BOTH the editor UI and the persisted payload.
  // Preserves the original index so the input handlers still target the right row.
  const sortedLines = useMemo(() => {
    return lines
      .map((r, __origIdx) => ({ ...r, __origIdx }))
      .sort((a, b) => {
        // rows without a date sink to the bottom (like ORDER BY date NULLS LAST)
        const ad = a.date || "9999-12-31";
        const bd = b.date || "9999-12-31";
        if (ad === bd) return a.__origIdx - b.__origIdx;
        return ad.localeCompare(bd);
      });
  }, [lines]);

  useEffect(() => {
    if (!customerId) return;
    const c = customers.find((x) => x.id === customerId);
    if (c) {
      setBillName(c.name || "");
      setBillAddress(c.address || "");
    }
  }, [customerId, customers]);

  const grossAmount = lines.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const sgstAmt = type === "gstin" ? +(grossAmount * (parseFloat(sgstRate) || 0) / 100).toFixed(2) : 0;
  const cgstAmt = type === "gstin" ? +(grossAmount * (parseFloat(cgstRate) || 0) / 100).toFixed(2) : 0;
  const totalGst = sgstAmt + cgstAmt;
  const grandTotal = +(grossAmount + totalGst).toFixed(2);

  const fetchFromDeliveries = async ({ cust_id, from, to, silent = false }) => {
    try {
      const r = await api.get("/customer-deliveries", { params: { customer_id: cust_id, from_date: from, to_date: to } });
      const dels = r.data || [];
      if (!dels.length) {
        if (!silent) toast.error("No deliveries in that range");
        return { count: 0 };
      }
      const newLines = dels
        .filter((d) => d.filled_given > 0)
        .map((d) => {
          const ct = cylTypes.find((t) => t.id === d.cylinder_type_id);
          return {
            description: ct ? ct.name : (d.cylinder_name || "Cylinder"),
            hsn_sac: "271119",
            date: d.date,
            qty: d.filled_given,
            unit: "Nos",
            rate: d.rate || 0,
            amount: +(((d.filled_given) * (d.rate || 0))).toFixed(2),
          };
        });
      if (!newLines.length) {
        if (!silent) toast.error("Deliveries in range have 0 filled cylinders");
        return { count: 0 };
      }
      setLines(newLines);
      const c = customers.find((x) => x.id === cust_id);
      if (c) { setCustomerId(cust_id); setBillName(c.name); setBillAddress(c.address || ""); }
      toast.success(`Auto-loaded ${newLines.length} line item${newLines.length > 1 ? "s" : ""} from deliveries`);
      setShowFetch(false);
      return { count: newLines.length };
    } catch (err) {
      if (!silent) toast.error(formatApiError(err.response?.data?.detail));
      return { count: 0 };
    }
  };

  // ── Auto-fetch: whenever customer + both dates are set, debounce 500ms and pull deliveries.
  useEffect(() => {
    if (isEdit) return;                       // don't clobber an existing invoice
    if (!customerId || !autoFrom || !autoTo) return;
    if (autoFrom > autoTo) return;
    const key = `${customerId}|${autoFrom}|${autoTo}`;
    if (key === lastAutoKey) return;
    const handle = setTimeout(async () => {
      setAutoFetching(true);
      const res = await fetchFromDeliveries({ cust_id: customerId, from: autoFrom, to: autoTo, silent: true });
      setAutoFetching(false);
      setLastAutoKey(key);
      if (res.count === 0) toast.message("No deliveries in that range — enter line items manually.");
    }, 500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, autoFrom, autoTo]);

  const submit = async (e) => {
    e.preventDefault();
    if (!billName.trim()) return toast.error("Bill To name required");
    if (!lines.some((r) => (parseFloat(r.qty) || 0) > 0 || r.description)) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        type, theme,
        invoice_no: invoiceNo || undefined,
        invoice_date: invoiceDate,
        customer_id: customerId || null,
        bill_to_name: billName.trim(), bill_to_address: billAddress, bill_to_gstin: billGstin,
        contact_name: contactName, name_of_work: nameOfWork,
        custom_title: type === "custom" ? customTitle.trim() : "",
        contact_name: contactName, name_of_work: nameOfWork,
        lines: lines.filter((r) => r.description || (parseFloat(r.qty) || 0) > 0)
                     .map((r) => ({
                       description: r.description || "",
                       hsn_sac: r.hsn_sac || "",
                       date: r.date || "",
                       qty: parseFloat(r.qty) || 0,
                       unit: r.unit || "",
                       rate: parseFloat(r.rate) || 0,
                       amount: parseFloat(r.amount) || 0,
                     })),
        sgst_rate: parseFloat(sgstRate) || 0,
        cgst_rate: parseFloat(cgstRate) || 0,
        igst_rate: 0,
        total_amount: grandTotal,
        note,
      };
      if (isEdit) await api.patch(`/invoices/${initial.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(isEdit ? "Invoice updated" : "Invoice created");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl rounded-none border-slate-300 max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-blue">{isEdit ? `Edit invoice · ${initial.invoice_no}` : "Create new invoice"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid="invoice-editor-form">
          {/* Type + theme + no + date */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="overline">Invoice type *</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="rounded-none" data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gstin">GST-Tax Invoice</SelectItem>
                  <SelectItem value="no_tax">Invoice</SelectItem>
                  <SelectItem value="challan">Delivery Challan</SelectItem>
                  <SelectItem value="custom">Custom Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {type === "custom" && (
              <div className="md:col-span-4">
                <Label className="overline">Custom title — will print at the top of the invoice</Label>
                <Input
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="e.g. Quotation · Delivery Note · Proforma Invoice"
                  className="rounded-none uppercase font-semibold tracking-wide"
                  data-testid="invoice-custom-title-input"
                  maxLength={80}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Leave empty to fall back to "Custom Invoice".
                </p>
              </div>
            )}
            <div>
              <Label className="overline">Theme</Label>
              <Select value={String(theme)} onValueChange={(v) => setTheme(parseInt(v))}>
                <SelectTrigger className="rounded-none" data-testid="invoice-theme-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(THEMES).map(([id, t]) => <SelectItem key={id} value={id}>{id}. {t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Invoice No.</Label>
              <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="Auto-generated" className="rounded-none font-mono-num" data-testid="invoice-no-input" />
            </div>
            <div>
              <Label className="overline">Invoice Date *</Label>
              <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="rounded-none" data-testid="invoice-date-input" />
            </div>
          </div>

            {/* Bill-To with customer picker + fetch + new customer buttons */}
          <div className="border border-slate-200 p-4 bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-brand-blue">Bill To</div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => setShowFetch(true)} data-testid="fetch-deliveries-btn">
                  <DownloadSimpleIcon size={12} className="mr-1" /> Fetch (manual)
                </Button>
                <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={() => setShowNewCustomer(true)} data-testid="new-customer-btn">
                  <UserPlusIcon size={12} className="mr-1" /> New customer
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="overline">Pick customer</Label>
                <Select value={customerId || "__none__"} onValueChange={(v) => setCustomerId(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="rounded-none" data-testid="invoice-customer-select"><SelectValue placeholder="Free text" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Free text —</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="overline">Bill to name *</Label>
                <Input value={billName} onChange={(e) => setBillName(e.target.value)} className="rounded-none" data-testid="invoice-billto-name" />
              </div>
            </div>
            {/* Inline auto-fetch range — only shown when a customer is picked */}
            {customerId && !isEdit && (
              <div className="mt-3 border border-brand-orange bg-brand-orange-tint/40 p-3" data-testid="invoice-auto-range">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline text-brand-orange-ink">Auto-fill from deliveries · pick a date range</div>
                  {autoFetching && <span className="overline text-brand-blue text-xs">Loading…</span>}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="overline">From</Label>
                    <Input type="date" value={autoFrom} onChange={(e) => setAutoFrom(e.target.value)} className="rounded-none" data-testid="invoice-autorange-from" />
                  </div>
                  <div>
                    <Label className="overline">Till</Label>
                    <Input type="date" value={autoTo} onChange={(e) => setAutoTo(e.target.value)} className="rounded-none" data-testid="invoice-autorange-to" />
                  </div>
                  <div className="col-span-2 flex items-end">
                    <p className="text-xs text-slate-600">
                      As soon as both dates are set, every delivery in this window will be pulled into the line items automatically. Just edit the range to refresh.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
              <div className="md:col-span-2">
                <Label className="overline">Delivery address</Label>
                <textarea rows={2} value={billAddress} onChange={(e) => setBillAddress(e.target.value)} className="w-full border border-slate-300 rounded-none px-3 py-2 text-sm outline-none focus:border-brand-blue" data-testid="invoice-billto-address" />
              </div>
              <div>
                <Label className="overline">Contact name (optional)</Label>
                <Input value={contactName} onChange={(e) => setContactName(e.target.value)} className="rounded-none" data-testid="invoice-contact-name" />
              </div>
              {type === "gstin" && (
                <div>
                  <Label className="overline">Customer GSTIN</Label>
                  <Input value={billGstin} onChange={(e) => setBillGstin(e.target.value)} className="rounded-none font-mono-num" data-testid="invoice-billto-gstin" />
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className="overline">Name of Work (heading above line items)</Label>
            <Input value={nameOfWork} onChange={(e) => setNameOfWork(e.target.value)} placeholder="e.g. 19KG COMMERCIAL DELIVERY" className="rounded-none" data-testid="invoice-work" />
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="overline">Line items · rows resize automatically</Label>
              <Button type="button" onClick={addLine} variant="outline" className="rounded-none h-8" data-testid="invoice-add-line">
                <PlusIcon size={12} className="mr-1" /> Row
              </Button>
            </div>
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-blue-tint">
                  <tr>
                    <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue w-10">S.N.</th>
                    <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue w-32">Date</th>
                    <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue">Description Of work</th>
                    <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue w-28">HSN/SAC</th>
                    <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue w-20">Qty</th>
                    <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue w-20">Unit</th>
                    <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue w-28">Rate</th>
                    <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue w-32">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLines.map((r, i) => (
                    <tr key={`l-${r.__key ?? i}`} className="border-b border-slate-100" data-testid={`invoice-line-${i}`}>
                      <td className="p-2 text-center font-mono-num text-slate-500">{i + 1}</td>
                      <td className="p-1">
                        <input
                          type="date"
                          value={r.date || ""}
                          onChange={(e) => setLine(r.__origIdx, { date: e.target.value })}
                          className="w-full border border-slate-300 rounded-none px-2 h-8 font-mono-num text-xs outline-none focus:border-brand-blue"
                          data-testid={`invoice-line-date-${i}`}
                        />
                      </td>
                      <td className="p-1">
                        <input list={`prod-${i}`} value={r.description} onChange={(e) => setLine(r.__origIdx, { description: e.target.value })} className="w-full border border-slate-300 rounded-none px-2 h-8 outline-none focus:border-brand-blue" data-testid={`invoice-line-desc-${i}`} />
                        <datalist id={`prod-${i}`}>
                          {cylTypes.map((t) => <option key={t.id} value={t.name} />)}
                          <option value="XTRA TEJ 19KG COMMERCIAL CYLINDER" />
                          <option value="5KG CYLINDER" />
                        </datalist>
                      </td>
                      <td className="p-1"><input value={r.hsn_sac || ""} onChange={(e) => setLine(r.__origIdx, { hsn_sac: e.target.value })} placeholder="271119" className="w-full border border-slate-300 rounded-none px-2 h-8 font-mono-num outline-none focus:border-brand-blue" data-testid={`invoice-line-hsn-${i}`} /></td>
                      <td className="p-1"><input type="number" step="0.01" min="0" value={r.qty} onChange={(e) => setLine(r.__origIdx, { qty: e.target.value })} className="w-full border border-slate-300 rounded-none px-2 h-8 text-right font-mono-num outline-none focus:border-brand-blue" data-testid={`invoice-line-qty-${i}`} /></td>
                      <td className="p-1"><input value={r.unit} onChange={(e) => setLine(r.__origIdx, { unit: e.target.value })} placeholder="EA" className="w-full border border-slate-300 rounded-none px-2 h-8 outline-none focus:border-brand-blue" data-testid={`invoice-line-unit-${i}`} /></td>
                      <td className="p-1"><input type="number" step="0.01" min="0" value={r.rate} onChange={(e) => setLine(r.__origIdx, { rate: e.target.value })} className="w-full border border-slate-300 rounded-none px-2 h-8 text-right font-mono-num outline-none focus:border-brand-blue" data-testid={`invoice-line-rate-${i}`} /></td>
                      <td className="p-1"><input type="number" step="0.01" min="0" value={r.amount} onChange={(e) => setLine(r.__origIdx, { amount: e.target.value })} className="w-full border border-slate-300 rounded-none px-2 h-8 text-right font-mono-num font-semibold outline-none focus:border-brand-blue" data-testid={`invoice-line-amount-${i}`} /></td>
                      <td className="p-1 text-center">
                        {lines.length > 1 && (<button type="button" onClick={() => removeLine(r.__origIdx)} className="text-slate-400 hover:text-[#FF2400]" data-testid={`invoice-line-delete-${i}`}><XIcon size={14} /></button>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-brand-blue">
                    <td colSpan={7} className="p-2 text-right font-bold uppercase text-xs tracking-wider text-brand-blue">Total Liability</td>
                    <td className="p-2 text-right font-mono-num font-black text-brand-blue" data-testid="invoice-liability">{money(grossAmount)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {type === "gstin" && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 border border-slate-200 p-3 bg-brand-orange-tint">
              <div>
                <Label className="overline">SGST rate (%)</Label>
                <Input type="number" step="0.01" value={sgstRate} onChange={(e) => setSgstRate(e.target.value)} className="rounded-none font-mono-num" data-testid="invoice-sgst-rate" />
              </div>
              <div>
                <Label className="overline">CGST rate (%)</Label>
                <Input type="number" step="0.01" value={cgstRate} onChange={(e) => setCgstRate(e.target.value)} className="rounded-none font-mono-num" data-testid="invoice-cgst-rate" />
              </div>
              <div className="col-span-2 grid grid-cols-2 gap-3 text-sm items-end">
                <div className="text-right"><span className="text-slate-500">SGST amount:</span> <span className="font-mono-num font-bold">{money(sgstAmt)}</span></div>
                <div className="text-right"><span className="text-slate-500">CGST amount:</span> <span className="font-mono-num font-bold">{money(cgstAmt)}</span></div>
                <div className="text-right col-span-2"><span className="text-slate-500 uppercase text-xs">Grand total:</span> <span className="font-mono-num font-black text-lg text-brand-blue">{money(grandTotal)}</span></div>
              </div>
            </div>
          )}

          <div>
            <Label className="overline">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="invoice-note" />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid="invoice-save-button">
              <ReceiptIcon size={14} className="mr-2" />
              {saving ? "Saving…" : isEdit ? "Update invoice" : "Create invoice"}
            </Button>
          </DialogFooter>
        </form>

        {showFetch && (
          <FetchDeliveriesDialog customers={customers} onFetch={fetchFromDeliveries} onClose={() => setShowFetch(false)} />
        )}
        {showNewCustomer && (
          <NewCustomerDialog
            onClose={() => setShowNewCustomer(false)}
            onCreated={(c) => {
              setShowNewCustomer(false);
              setCustomerId(c.id); setBillName(c.name); setBillAddress(c.address || "");
              onCustomerAdded();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════ FETCH ═══════════════════════════ */
function FetchDeliveriesDialog({ customers, onFetch, onClose }) {
  const today = iso(new Date());
  const [cid, setCid] = useState("");
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(today);
  const submit = (e) => {
    e.preventDefault();
    if (!cid) return toast.error("Pick a customer");
    onFetch({ cust_id: cid, from, to });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="fetch-dialog">
        <DialogHeader><DialogTitle>Fetch deliveries → invoice lines</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="overline">Customer</Label>
            <Select value={cid} onValueChange={setCid}>
              <SelectTrigger className="rounded-none" data-testid="fetch-customer"><SelectValue placeholder="Pick customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none" data-testid="fetch-from" /></div>
            <div><Label className="overline">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none" data-testid="fetch-to" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-blue text-white" data-testid="fetch-submit">Fetch</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════ NEW CUSTOMER ═══════════════════════════ */
function NewCustomerDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [rate, setRate] = useState("0");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const r = await api.post("/customers", { name: name.trim(), phone, address, category: "commercial", rate: parseFloat(rate) || 0, discount: 0, credit_limit: 0, opening_balance: 0 });
      toast.success("Customer added");
      onCreated(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="new-customer-dialog">
        <DialogHeader><DialogTitle>Add new customer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="overline">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none" data-testid="nc-name" /></div>
          <div><Label className="overline">Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none font-mono-num" data-testid="nc-phone" /></div>
          <div><Label className="overline">Address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-none" data-testid="nc-address" /></div>
          <div><Label className="overline">Default rate (₹)</Label><Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="nc-rate" /></div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-brand-blue text-white" data-testid="nc-save">{saving ? "Saving…" : "Add customer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════ PRINT MODAL ═══════════════════════════ */
function InvoicePrintModal({ invoice, profile, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  const downloadPdf = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const node = printRef.current;
      // Wait one frame so the freshly-rendered signature <img> is decoded
      await new Promise((r) => requestAnimationFrame(r));
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      // Fit the canvas inside A4 with 8mm margin, preserve aspect ratio
      const margin = 8;
      const maxW = pdfW - margin * 2;
      const maxH = pdfH - margin * 2;
      const imgRatio = canvas.width / canvas.height;
      let drawW = maxW;
      let drawH = drawW / imgRatio;
      if (drawH > maxH) { drawH = maxH; drawW = drawH * imgRatio; }
      const x = (pdfW - drawW) / 2;
      pdf.addImage(imgData, "PNG", x, margin, drawW, drawH);
      pdf.save(`${invoice.invoice_no || "invoice"}.pdf`);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF download failed", err);
      toast.error("Failed to build PDF. Try Print → Save as PDF as a fallback.");
    } finally { setDownloading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl rounded-none border-slate-300 max-h-[95vh] overflow-y-auto p-0">
        <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between no-print gap-3 flex-wrap">
          <DialogTitle className="font-display text-brand-blue">Invoice preview · {invoice.invoice_no}</DialogTitle>
          <div className="flex gap-2">
            <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="rounded-none border-brand-blue text-brand-blue hover:bg-brand-blue-tint" data-testid="invoice-download-pdf-btn">
              <FilePdfIcon size={14} className="mr-2" />
              {downloading ? "Building PDF…" : "Download PDF"}
            </Button>
            <Button onClick={() => window.print()} className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="invoice-print-btn">
              <PrinterIcon size={14} className="mr-2" /> Print
            </Button>
          </div>
        </div>
        <div ref={printRef} className="print-area bg-white p-4">
          <InvoicePrintTemplate invoice={invoice} profile={profile} />
        </div>
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            .print-area, .print-area * { visibility: visible !important; }
            .print-area { position: absolute; inset: 0; margin: 0 !important; padding: 8mm !important; background: white; }
            .no-print { display: none !important; }
            @page { size: A4 portrait; margin: 0; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════════ PRINT TEMPLATE (exact) ═══════════════════════════ */
function InvoicePrintTemplate({ invoice, profile }) {
  const p = profile || {};
  const t = THEMES[invoice.theme || 1] || THEMES[1];
  const isGst = invoice.type === "gstin";
  const isChallan = invoice.type === "challan";
  // Sort lines by date ascending (empty dates last).
  const sortedLines = [...(invoice.lines || [])].sort((a, b) => {
    const ad = a.date || "9999-12-31";
    const bd = b.date || "9999-12-31";
    return ad.localeCompare(bd);
  });
  const gross = sortedLines.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const sgst = isGst ? +(gross * (invoice.sgst_rate || 0) / 100).toFixed(2) : 0;
  const cgst = isGst ? +(gross * (invoice.cgst_rate || 0) / 100).toFixed(2) : 0;
  const totalGst = sgst + cgst;
  const grand = +(gross + totalGst).toFixed(2);

  const B = t.border;
  const A = t.accent;
  const H = t.head;

  // Resolve signature URL — a served-path (starts with /api) needs the backend origin.
  const rawSig = p.signature_url || "";
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const sigSrc = rawSig
    ? (rawSig.startsWith("http") ? rawSig : `${backend}${rawSig}`)
    : "";

  // DD/MM/YYYY for the invoice line-item date column
  const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = String(iso).slice(0, 10).split("-");
    return d && m && y ? `${d}/${m}/${y}` : iso;
  };

  // Style helpers keyed to selected theme:
  const cellBase = { border: `1px solid ${B}`, padding: "3px 5px", fontSize: 13, color: t.ink };
  const cellHead = { ...cellBase, background: H, fontWeight: 700, textAlign: "center" };
  const dataRow = { border: `1px solid ${B}`, padding: "3px 5px", fontSize: 13, color: t.ink };

  return (
    <div style={{ fontFamily: "Arial, Helvetica, sans-serif", color: t.ink, border: `1px solid ${B}` }}>
      {/* Header: logos left + big business name centered */}
      <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", borderBottom: `1px solid ${B}` }}>
        <div style={{ padding: 6, borderRight: `1px solid ${B}`, display: "flex", alignItems: "center", gap: 4, justifyContent: "center", background: "#fff" }}>
          <img src={INDIAN_OIL_LOGO} alt="IOC" style={{ maxWidth: 44, maxHeight: 44 }} />
          <img src={INDANE_LOGO} alt="Indane" style={{ maxWidth: 90, maxHeight: 42 }} />
        </div>
        <div style={{ textAlign: "center", padding: "6px 8px" }}>
          <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 1, color: t.ink }}>{p.business_name || "SHREE SAI SAYAJI INDANE"}</div>
          {p.address && <div style={{ fontSize: 14, marginTop: 2, color: t.ink }}>{p.address}</div>}
        </div>
      </div>

      {/* Invoice type band with invoice-no cell */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: `1px solid ${B}` }}>
        <div style={{ padding: 4, textAlign: "center", fontSize: 14, fontWeight: 700, borderRight: `1px solid ${B}`, color: t.ink }}>{
          invoice.type === "custom" && (invoice.custom_title || "").trim()
            ? invoice.custom_title.trim().toUpperCase()
            : (TYPE_LABEL[invoice.type] || invoice.type)
        }</div>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr" }}>
          <div style={{ padding: 4, borderRight: `1px solid ${B}`, fontWeight: 700, fontSize: 13 }}>Invoice No :</div>
          <div style={{ padding: 4, fontSize: 13, fontFamily: "monospace" }}>{invoice.invoice_no}</div>
        </div>
      </div>

      {/* Bill-to (left) + Vendor (right) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: `1px solid ${B}` }}>
        {/* LEFT — Bill-to */}
        <div style={{ borderRight: `1px solid ${B}` }}>
          <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", borderBottom: `1px solid ${B}` }}>
            <div style={{ padding: 4, borderRight: `1px solid ${B}`, fontWeight: 700, fontSize: 13 }}>Bill To :</div>
            <div style={{ padding: 4, fontWeight: 700, fontSize: 13 }}>{invoice.bill_to_name}</div>
          </div>
          <div style={{ padding: 6, fontSize: 13 }}>
            <div style={{ fontWeight: 700, textAlign: "center" }}>DELIVERY ADDRESS :</div>
            <div style={{ marginTop: 3, textAlign: "center", whiteSpace: "pre-line" }}>{invoice.bill_to_address || "—"}</div>
            {invoice.contact_name && (
              <div style={{ marginTop: 4, textAlign: "center" }}>NAME : {invoice.contact_name}</div>
            )}
          </div>
        </div>

        {/* RIGHT — Vendor */}
        <div>
          <VRow B={B} label="Invoice Date:" value={invoice.invoice_date} />
          {isGst && <VRow B={B} label="VENDOR GSTN:" value={p.gstin || "—"} />}
          <VRow B={B} label="VENDOR NAME:" value={p.vendor_name || "—"} />
          <VRow B={B} label="PHONE NUMBER:" value={p.phone || "—"} />
          <VRow B={B} label="EMAIL ID:" value={p.email || "—"} link accent={A} />
          <VRow B={B} label="IOCL VENDOR CODE:" value={p.iocl_vendor_code || "—"} />
          {p.iocl_po && <VRow B={B} label="IOCL PO:" value={p.iocl_po} />}
          {p.line_item && <VRow B={B} label="LINE ITEM:" value={p.line_item} />}
          <VRow B={B} label="COMPANY CODE:" value={p.company_code || "—"} last />
        </div>
      </div>

      {/* Plant Code + Currency */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: `1px solid ${B}` }}>
        <div style={{ padding: 4, textAlign: "center", fontSize: 13, borderRight: `1px solid ${B}` }}>
          <span style={{ fontWeight: 700 }}>PLANT CODE :</span> {dash(p.plant_code)}
        </div>
        <div style={{ padding: 4, textAlign: "center", fontSize: 13 }}>
          <span style={{ fontWeight: 700 }}>CURRENCY :</span> {p.currency || "INR"}
        </div>
      </div>

      {/* NAME OF WORK header */}
      {invoice.name_of_work && (
        <div style={{ padding: 4, borderBottom: `1px solid ${B}`, fontSize: 14, fontWeight: 700 }}>
          NAME OF WORK :- <span style={{ marginLeft: 8, fontWeight: 700 }}>{invoice.name_of_work}</span>
        </div>
      )}

      {/* Line-items table (grows with rows) */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...cellHead, width: 32 }}>S.N.</th>
            <th style={{ ...cellHead, width: 78 }}>Date</th>
            <th style={{ ...cellHead, textAlign: "left" }}>Description Of work</th>
            <th style={{ ...cellHead, width: 90 }}>HSN/SAC</th>
            <th style={{ ...cellHead, width: 46 }}>Qty</th>
            <th style={{ ...cellHead, width: 40 }}>Unit</th>
            <th style={{ ...cellHead, width: 62 }}>Rate</th>
            <th style={{ ...cellHead, width: 80 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {sortedLines.map((ln, i) => (
            <tr key={`ln-${i}`}>
              <td style={{ ...dataRow, textAlign: "center" }}>{i + 1}</td>
              <td style={{ ...dataRow, textAlign: "center", fontFamily: "monospace", fontSize: 12 }}>{fmtDate(ln.date)}</td>
              <td style={{ ...dataRow, textAlign: "left" }}>{ln.description}</td>
              <td style={{ ...dataRow, textAlign: "center", fontFamily: "monospace" }}>{ln.hsn_sac || ""}</td>
              <td style={{ ...dataRow, textAlign: "right" }}>{ln.qty}</td>
              <td style={{ ...dataRow, textAlign: "center" }}>{ln.unit}</td>
              <td style={{ ...dataRow, textAlign: "right" }}>{fmt(ln.rate)}</td>
              <td style={{ ...dataRow, textAlign: "right" }}>{fmt(ln.amount)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={7} style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>Total Liability</td>
            <td style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>{fmt(gross)}</td>
          </tr>
        </tbody>
      </table>

      {/* Tax breakdown (only for GSTIN) */}
      {isGst && (
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: `1px solid ${B}` }}>
          <tbody>
            <tr>
              <td colSpan={4} style={{ ...dataRow, textAlign: "center", fontWeight: 700 }}>Calculation of Taxable Value</td>
            </tr>
            <tr>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>Gross Amount (Excluding G S T)</td>
              <td style={{ ...dataRow, width: 60, textAlign: "center" }}>Rs.</td>
              <td style={{ ...dataRow, width: 100, textAlign: "right" }}>-</td>
              <td style={{ ...dataRow, width: 100, textAlign: "right" }}>{fmt(gross)}</td>
            </tr>
            <tr>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>SGST @ {invoice.sgst_rate || 0}%</td>
              <td style={{ ...dataRow, textAlign: "center" }}>Rs.</td>
              <td style={{ ...dataRow, textAlign: "right" }}>-</td>
              <td style={{ ...dataRow, textAlign: "right" }}>{fmt(sgst)}</td>
            </tr>
            <tr>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>CGST @ {invoice.cgst_rate || 0}%</td>
              <td style={{ ...dataRow, textAlign: "center" }}>Rs.</td>
              <td style={{ ...dataRow, textAlign: "right" }}>-</td>
              <td style={{ ...dataRow, textAlign: "right" }}>{fmt(cgst)}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ ...dataRow }}></td>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>TOTAL GST AMOUNT</td>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 700 }}>{fmt(totalGst)}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ ...dataRow, borderRight: 0 }}></td>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 900, fontSize: 15 }}>TOTAL</td>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 900, fontSize: 15 }}>₹ {fmt(grand)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Non-GST grand total */}
      {!isGst && (
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: `1px solid ${B}` }}>
          <tbody>
            <tr>
              <td colSpan={2} style={{ ...dataRow, textAlign: "right", fontWeight: 900, fontSize: 15, width: "70%" }}>{isChallan ? "TOTAL QTY / VALUE" : "TOTAL"}</td>
              <td style={{ ...dataRow, textAlign: "right", fontWeight: 900, fontSize: 15 }}>₹ {fmt(gross)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Footer note + signatory */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", borderTop: `1px solid ${B}` }}>
        <div style={{ padding: 8, textAlign: "center", fontSize: 12, borderRight: `1px solid ${B}`, whiteSpace: "pre-line" }}>
          {p.footer_note || ""}
        </div>
        <div style={{ padding: 8, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", minHeight: 80, position: "relative" }}>
          {sigSrc && <img crossOrigin="anonymous" src={sigSrc} alt="sig" style={{ maxHeight: 44, marginBottom: 4 }} />}
          <div style={{ borderTop: sigSrc ? "none" : `1px dashed ${B}`, width: "80%", paddingTop: 4, fontSize: 13, fontWeight: 700 }}>Authorized Signatory</div>
        </div>
      </div>

      {p.bank_ac_no || p.bank_ifsc ? (
        <div style={{ padding: "4px 8px", fontSize: 12, borderTop: `1px solid ${B}`, background: H }}>
          <span style={{ fontWeight: 700 }}>Payment Details :</span>{" "}
          A/C {p.bank_ac_no} · {p.bank_ac_name} · IFSC {p.bank_ifsc}
        </div>
      ) : null}
    </div>
  );
}

function VRow({ B, label, value, link, accent, last }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", borderBottom: last ? "none" : `1px solid ${B}` }}>
      <div style={{ padding: 3, borderRight: `1px solid ${B}`, fontWeight: 700, fontSize: 13 }}>{label}</div>
      <div style={{ padding: 3, fontSize: 13, fontFamily: link ? "Arial" : "monospace", color: link ? (accent || "#1e40af") : "inherit", textDecoration: link ? "underline" : "none" }}>{value}</div>
    </div>
  );
}
